/**
 * LiveCityNightlyTile — iter267
 *
 * Per-city live nightly metrics tile for the admin Pipeline & Queues group.
 * Shows three numbers per city, refreshed every 30s:
 *   - queue_size            (birthday_research_queue depth)
 *   - turbo_offers_today    (birthday_offers w/ source=turbo_birthday in last 24h)
 *   - cost_today_usd        (Phase 6 LLM spend in last 24h)
 *
 * Cities with `has_critical_alert_today=true` get a red ring so you can spot
 * orchestrator-flagged runs at a glance.
 *
 * No new endpoints — uses GET /api/admin/pipeline/cities (extended in iter267).
 */
import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

const POLL_MS = 30_000;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Format an ISO timestamp into a Pacific-time clock label like "22:14 PDT"
 * (or "22:14 PST" depending on the date). Matches the founder-requested
 * tooltip format from the iter268 spec. Returns empty string on bad input.
 */
function formatPacificClock(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const parts = new Intl.DateTimeFormat("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "America/Los_Angeles",
      timeZoneName: "short",
    }).formatToParts(d);
    const hour = parts.find((p) => p.type === "hour")?.value || "00";
    const minute = parts.find((p) => p.type === "minute")?.value || "00";
    const tz = parts.find((p) => p.type === "timeZoneName")?.value || "PT";
    return `${hour}:${minute} ${tz}`;
  } catch {
    return "";
  }
}

/**
 * Build the tooltip body for a city's most-recent critical alert. Returns
 * a short headline ("Phase 1 zero candidates at 22:14 PDT") plus the full
 * reason list so the user sees both the gist and the detail.
 */
function buildAlertTooltip(alert) {
  if (!alert) return null;
  const isPhase1 = (alert.event_type || "").startsWith("phase1");
  const phaseLabel = isPhase1 ? "Phase 1" : "Phase 6";
  const clock = formatPacificClock(alert.timestamp);
  const reason = (alert.reason || "Critical alert fired").toString();
  // Headline picks the first reason for compactness; full reason kept below.
  const firstReason = reason.split(";")[0].trim();
  return {
    headline: `${phaseLabel} ${firstReason || "critical alert"}${clock ? ` at ${clock}` : ""}`,
    fullReason: reason,
    eventType: alert.event_type,
    clock,
  };
}

export default function LiveCityNightlyTile() {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  const fetchCities = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/admin/pipeline/cities`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      setCities(
        (data.cities || []).filter((c) => c.active !== false)
      );
      setError(null);
      setLastFetched(new Date());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCities();
    const interval = setInterval(fetchCities, POLL_MS);
    return () => clearInterval(interval);
  }, []);

  const totals = useMemo(() => {
    return cities.reduce(
      (acc, c) => ({
        queue: acc.queue + (c.queue_size || 0),
        offers: acc.offers + (c.turbo_offers_today || 0),
        cost: acc.cost + (c.cost_today_usd || 0),
      }),
      { queue: 0, offers: 0, cost: 0 }
    );
  }, [cities]);

  if (loading) {
    return (
      <div
        className="rounded-lg border border-slate-200 bg-white p-4"
        data-testid="live-city-nightly-tile-loading"
      >
        <div className="text-xs text-slate-400">Loading nightly metrics…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="rounded-lg border border-rose-200 bg-rose-50 p-4"
        data-testid="live-city-nightly-tile-error"
      >
        <div className="text-xs text-rose-700">Live tile error: {error}</div>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
    <div
      className="rounded-lg border border-slate-200 bg-white overflow-hidden"
      data-testid="live-city-nightly-tile"
    >
      {/* Header */}
      <div className="flex items-baseline justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-200">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">
            Tonight's Nightly Run — Live
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Per-city queue, offers, and LLM spend. Refreshes every 30s.
          </p>
        </div>
        <div className="text-right">
          <div className="text-[11px] text-slate-500">
            {lastFetched
              ? `Updated ${lastFetched.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })}`
              : ""}
          </div>
          <div className="flex gap-3 mt-0.5 text-[11px]">
            <span data-testid="live-tile-total-queue">
              <span className="text-slate-500">Σ queue:</span>{" "}
              <span className="font-semibold text-slate-800">{totals.queue.toLocaleString()}</span>
            </span>
            <span data-testid="live-tile-total-offers">
              <span className="text-slate-500">Σ offers:</span>{" "}
              <span className="font-semibold text-emerald-700">{totals.offers}</span>
            </span>
            <span data-testid="live-tile-total-cost">
              <span className="text-slate-500">Σ cost:</span>{" "}
              <span className="font-semibold text-amber-700">${totals.cost.toFixed(4)}</span>
            </span>
          </div>
        </div>
      </div>

      {/* City rows */}
      <div className="divide-y divide-slate-100">
        {cities.length === 0 ? (
          <div className="px-4 py-6 text-center text-xs text-slate-400 italic">
            No active cities in pipeline.
          </div>
        ) : (
          cities.map((city) => {
            const isCritical = city.has_critical_alert_today;
            const alertInfo = buildAlertTooltip(city.last_critical_alert);
            const row = (
              <motion.div
                key={city.city_id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={`grid grid-cols-12 gap-3 items-center px-4 py-2.5 hover:bg-slate-50 transition ${
                  isCritical ? "ring-1 ring-rose-300 bg-rose-50/50 cursor-help" : ""
                }`}
                data-testid={`live-city-row-${city.city_id}`}
              >
                {/* City name */}
                <div className="col-span-4 min-w-0">
                  <div className="flex items-center gap-2">
                    {isCritical && (
                      <span
                        className="inline-block w-1.5 h-1.5 rounded-full bg-rose-500 flex-shrink-0"
                        data-testid={`critical-dot-${city.city_id}`}
                      />
                    )}
                    <div className="truncate">
                      <div className="text-sm font-medium text-slate-800 truncate">
                        {city.city_name}
                      </div>
                      <div className="text-[10px] text-slate-400 truncate">
                        {city.msa_name || `${city.state_code}`}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Queue */}
                <div className="col-span-3 text-right">
                  <div
                    className="text-sm font-semibold text-slate-800"
                    data-testid={`queue-size-${city.city_id}`}
                  >
                    {(city.queue_size || 0).toLocaleString()}
                  </div>
                  <div className="text-[10px] text-slate-400">queued</div>
                </div>

                {/* Offers */}
                <div className="col-span-2 text-right">
                  <div
                    className="text-sm font-semibold text-emerald-700"
                    data-testid={`offers-today-${city.city_id}`}
                  >
                    {city.turbo_offers_today || 0}
                  </div>
                  <div className="text-[10px] text-slate-400">offers 24h</div>
                </div>

                {/* Cost */}
                <div className="col-span-3 text-right">
                  <div
                    className="text-sm font-semibold text-amber-700 tabular-nums"
                    data-testid={`cost-today-${city.city_id}`}
                  >
                    ${(city.cost_today_usd || 0).toFixed(4)}
                  </div>
                  <div className="text-[10px] text-slate-400">cost 24h</div>
                </div>
              </motion.div>
            );

            // iter268 — tooltip on red-ringed cities. Pulls reason +
            // PDT/PST timestamp from `last_critical_alert` already returned
            // by /api/admin/pipeline/cities; no new endpoint.
            if (isCritical && alertInfo) {
              return (
                <Tooltip key={city.city_id} delayDuration={150}>
                  <TooltipTrigger asChild>{row}</TooltipTrigger>
                  <TooltipContent
                    side="top"
                    align="start"
                    className="max-w-xs bg-rose-950 text-rose-50 border-rose-800"
                    data-testid={`critical-alert-tooltip-${city.city_id}`}
                  >
                    <div className="space-y-1">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-rose-200">
                        {alertInfo.eventType === "phase6_critical_alert"
                          ? "Phase 6 critical alert"
                          : "Phase 1 critical alert"}
                      </div>
                      <div
                        className="text-xs leading-snug"
                        data-testid={`critical-alert-headline-${city.city_id}`}
                      >
                        {alertInfo.headline}
                      </div>
                      {alertInfo.fullReason &&
                        alertInfo.fullReason !== alertInfo.headline.replace(
                          ` at ${alertInfo.clock}`,
                          ""
                        ) && (
                          <div
                            className="text-[10px] text-rose-200/80 leading-snug pt-0.5 border-t border-rose-800"
                            data-testid={`critical-alert-detail-${city.city_id}`}
                          >
                            {alertInfo.fullReason}
                          </div>
                        )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              );
            }
            return row;
          })
        )}
      </div>
    </div>
    </TooltipProvider>
  );
}
